
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import usePublicPetsByCategoryWithOwners from '../hooks/usePublicPetsWithOwners';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

const PetsByCategory = () => {
  const { category } = useParams<{ category: string }>();
  const { t } = useTranslation();
  const { data: pets, isLoading, error, refetch } = usePublicPetsByCategoryWithOwners(category);

  const getCategoryLabel = (categoryKey: string): string => {
    const categoryLabels: Record<string, string> = {
      'dog': t('categories.dogs'),
      'cat': t('categories.cats'), 
      'bird': t('categories.birds'),
      'fish': t('categories.fish'),
      'reptile': t('categories.reptiles'),
      'small_pet': t('categories.smallPets'),
      'horse': t('categories.horses'),
      'cattle': t('categories.cattle'),
      'other': t('categories.other'),
      'all': t('categories.allPets')
    };
    return categoryLabels[categoryKey] || t('categories.allPets');
  };

  const categoryLabel = getCategoryLabel(category || 'all');

  if (error) {
    return (
      <div className="container mx-auto max-w-2xl p-4 py-8 pb-20">
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">Error loading pets: {error.message}</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl p-4 py-8 pb-20">
      <div className="mb-6 flex items-center gap-4">
        <Link to="/">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">{categoryLabel}</h1>
        {pets && (
          <span className="text-sm text-gray-500">
            ({pets.length} pets found)
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-lg overflow-hidden border bg-card">
              <Skeleton className="h-48 w-full" />
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : pets && pets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pets.map((pet: any) => {
            const owner = pet.users;
            const ownerName = owner?.full_name || owner?.email?.split('@')[0] || 'Pet Owner';
            const ownerAvatar = owner?.avatar_url;
            
            return (
              <div key={pet.id} className="rounded-lg overflow-hidden border bg-card hover:shadow-lg transition-shadow">
                <div className="h-48 bg-gray-50 relative flex items-center justify-center">
                  {pet.photo_url ? (
                    <img
                      src={pet.photo_url}
                      alt={pet.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-pet-purple/10">
                      <span className="text-4xl text-pet-purple">
                        {pet.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  {pet.is_memorial && (
                    <Badge className="absolute top-2 right-2 bg-gray-500">
                      In Memory
                    </Badge>
                  )}
                </div>
                
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="h-8 w-8">
                      {ownerAvatar ? (
                        <AvatarImage src={ownerAvatar} alt={ownerName} />
                      ) : (
                        <AvatarFallback className="bg-pet-purple/20 text-pet-purple text-xs">
                          {ownerName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <span className="text-sm text-gray-600 font-medium">
                      {ownerName}
                    </span>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1">{pet.name}</h3>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="capitalize">{translateSpeciesSingular(pet.species, t)}</span>
                    {pet.breed && (
                      <>
                        <span>•</span>
                        <span>{pet.breed}</span>
                      </>
                    )}
                  </div>
                  
                  <div className="mt-3 flex justify-between items-center">
                    <Link to={`/pet/${pet.id}`}>
                      <Button variant="outline" size="sm">
                        View Profile
                      </Button>
                    </Link>
                    <span className="text-xs text-gray-400">
                      Privacy: {pet.privacy_setting}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-medium mb-2">No pets found</h3>
          <p className="text-gray-500 mb-6">
            No public pets in this category yet. Be the first to share your pet!
          </p>
          <Link to="/my-pets">
            <Button>Add Your Pet</Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default PetsByCategory;
